import { c as _c } from "react/compiler-runtime";
import { toast } from '@repo/design-system/components/ui/toaster';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectCustomPinEditingState, setCustomPinEditingState } from '@repo/store/maps/slices/mapData';
import { DEFAULT_PIN_COLOR } from '@repo/store/maps/utils/mapData';
import { useEffect, useState } from 'react';
import { useCustomPinOperations } from '../../game-map/hooks/useCustomPinOperations';
interface LocationData {
  externalId: string;
  title?: string;
  description?: string;
  color?: string;
}
interface EditingState {
  title: string;
  description: string;
  color: string;
}
export const useLocationEditingState = selectedLocation => {
  const $ = _c(35);
  const dispatch = useAppDispatch();
  const {
    isEditingCustomPin,
    editingPinId,
    handleSaveCustomPin,
    handleCancelCustomPin
  } = useCustomPinOperations();
  const {
    isNewlyCreated: isEditingNewlyCreatedPin,
    pinData
  } = useAppSelector(selectCustomPinEditingState);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      title: "",
      description: "",
      color: DEFAULT_PIN_COLOR
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [editingState, setEditingState] = useState(t0);
  const isCurrentlyEditing = isEditingCustomPin && (editingPinId === selectedLocation?.externalId || isEditingNewlyCreatedPin);
  let t1;
  let t2;
  if ($[1] !== isEditingCustomPin || $[2] !== isEditingNewlyCreatedPin || $[3] !== pinData || $[4] !== selectedLocation) {
    t1 = () => {
      if (selectedLocation) {
        setEditingState({
          title: selectedLocation.title || "",
          description: selectedLocation.description || "",
          color: selectedLocation.color || DEFAULT_PIN_COLOR
        });
      } else {
        if (isEditingNewlyCreatedPin && isEditingCustomPin && pinData) {
          setEditingState({
            title: pinData.title || "",
            description: pinData.description || "",
            color: pinData.color || DEFAULT_PIN_COLOR
          });
        }
      }
    };
    t2 = [selectedLocation, isEditingNewlyCreatedPin, isEditingCustomPin, pinData];
    $[1] = isEditingCustomPin;
    $[2] = isEditingNewlyCreatedPin;
    $[3] = pinData;
    $[4] = selectedLocation;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[7] !== dispatch || $[8] !== isEditingCustomPin || $[9] !== isEditingNewlyCreatedPin || $[10] !== selectedLocation) {
    t3 = () => {
      if (!selectedLocation && isEditingCustomPin && !isEditingNewlyCreatedPin) {
        dispatch(setCustomPinEditingState({
          isEditing: false,
          pinId: null,
          isNewlyCreated: false,
          pinData: null
        }));
      }
    };
    t4 = [selectedLocation, isEditingCustomPin, isEditingNewlyCreatedPin, dispatch];
    $[7] = dispatch;
    $[8] = isEditingCustomPin;
    $[9] = isEditingNewlyCreatedPin;
    $[10] = selectedLocation;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[13] !== dispatch) {
    t5 = () => () => {
      dispatch(setCustomPinEditingState({
        isEditing: false,
        pinId: null,
        isNewlyCreated: false,
        pinData: null
      }));
    };
    t6 = [dispatch];
    $[13] = dispatch;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = title => {
      setEditingState(prev => ({
        ...prev,
        title
      }));
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const updateTitle = t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = description => {
      setEditingState(prev_0 => ({
        ...prev_0,
        description
      }));
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const updateDescription = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = color => {
      setEditingState(prev_1 => ({
        ...prev_1,
        color
      }));
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const updateColor = t9;
  let t10;
  if ($[19] !== editingState.color || $[20] !== editingState.description || $[21] !== editingState.title || $[22] !== handleSaveCustomPin) {
    t10 = async () => {
      try {
        await handleSaveCustomPin({
          title: editingState.title,
          description: editingState.description,
          color: editingState.color
        });
      } catch {
        toast({
          title: "Failed to save location.",
          description: "There was an error saving the location, please try again.",
          variant: "error"
        });
      }
    };
    $[19] = editingState.color;
    $[20] = editingState.description;
    $[21] = editingState.title;
    $[22] = handleSaveCustomPin;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const handleSave = t10;
  let t11;
  if ($[24] !== handleCancelCustomPin) {
    t11 = async () => {
      try {
        await handleCancelCustomPin();
      } catch {
        toast({
          title: "Failed to cancel location edit.",
          description: "There was an error canceling the location edit, please try again.",
          variant: "error"
        });
      }
    };
    $[24] = handleCancelCustomPin;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const handleCancel = t11;
  let t12;
  if ($[26] !== editingState.title) {
    t12 = editingState.title.trim();
    $[26] = editingState.title;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = !t12;
  let t14;
  if ($[28] !== editingState || $[29] !== handleCancel || $[30] !== handleSave || $[31] !== isCurrentlyEditing || $[32] !== isEditingNewlyCreatedPin || $[33] !== t13) {
    t14 = {
      isCurrentlyEditing,
      editingState,
      updateTitle,
      updateDescription,
      updateColor,
      handleSave,
      handleCancel,
      isSaveDisabled: t13,
      isEditingNewlyCreatedPin
    };
    $[28] = editingState;
    $[29] = handleCancel;
    $[30] = handleSave;
    $[31] = isCurrentlyEditing;
    $[32] = isEditingNewlyCreatedPin;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
};